import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { protectedResources } from 'src/app/auth/auth.config';
import { ObservableTtlCache } from 'src/app/utils/observable-ttl-cache';
import { SKIP_GLOBAL_LOADING } from '../interceptor/http-context.tokens';
import {
  DeviceDto,
  DeviceListDto,
  DeviceProjection,
  DeviceUpdate,
  PartListDto,
  SessionListDto,
} from '../models/device.model';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private apiEndpoint: string = protectedResources.apiTodoList.endpoint;
  private readonly deviceCacheTtlMs = 60_000;
  private readonly deviceSessionsCache = new ObservableTtlCache<SessionListDto>(
    this.deviceCacheTtlMs,
  );
  private readonly devicePartsCache = new ObservableTtlCache<PartListDto>(
    this.deviceCacheTtlMs,
  );

  constructor(private http: HttpClient) {}

  private normalizeDevice(device: DeviceDto): DeviceDto {
    return {
      ...device,
      licenseBatches: device.licenseBatches ?? [],
    };
  }

  private normalizeDeviceList(response: DeviceListDto): DeviceListDto {
    return {
      ...response,
      devices: (response.devices ?? []).map((device) =>
        this.normalizeDevice(device),
      ),
    };
  }

  getCompanyDevices(
    projection: DeviceProjection = 'BRIEF',
  ): Observable<DeviceListDto> {
    const params = new HttpParams().set('projection', projection);
    return this.http
      .get<DeviceListDto>(`${this.apiEndpoint}/device`, { params })
      .pipe(map((response) => this.normalizeDeviceList(response)));
  }

  getAllDevices(
    projection: DeviceProjection = 'BRIEF',
  ): Observable<DeviceListDto> {
    const params = new HttpParams().set('projection', projection);
    return this.http
      .get<DeviceListDto>(`${this.apiEndpoint}/device/all`, { params })
      .pipe(map((response) => this.normalizeDeviceList(response)));
  }

  getDevicesByCompanyId(
    companyId: string,
    projection: DeviceProjection = 'BRIEF',
  ): Observable<DeviceListDto> {
    const normalizedCompanyId = companyId?.trim().toLowerCase();
    if (!normalizedCompanyId) {
      return of({ devices: [] });
    }
    return this.http
      .get<DeviceListDto>(
        `${this.apiEndpoint}/device/all?companyId=${normalizedCompanyId}&projection=${projection}`,
      )
      .pipe(map((response) => this.normalizeDeviceList(response)));
  }

  getDeviceParts(deviceId: string): Observable<PartListDto> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.devicePartsCache.get(encodedDeviceId, () =>
      this.http.get<PartListDto>(
        `${this.apiEndpoint}/device/${encodedDeviceId}/parts`,
      ),
    );
  }

  getDeviceSessions(deviceId: string): Observable<SessionListDto> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.deviceSessionsCache.get(encodedDeviceId, () =>
      this.http.get<SessionListDto>(
        `${this.apiEndpoint}/device/${encodedDeviceId}/sessions`,
      ),
    );
  }

  getDevice(
    deviceId: string,
    projection: DeviceProjection = 'FULL',
  ): Observable<DeviceDto> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    const params = new HttpParams().set('projection', projection);
    return this.http
      .get<DeviceDto>(`${this.apiEndpoint}/device/${encodedDeviceId}`, {
        params,
      })
      .pipe(map((device) => this.normalizeDevice(device)));
  }

  updateDevice(deviceId: string, payload: DeviceUpdate): Observable<DeviceDto> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.http
      .patch<DeviceDto>(
        `${this.apiEndpoint}/device/${encodedDeviceId}`,
        payload,
      )
      .pipe(map((device) => this.normalizeDevice(device)));
  }

  previewDeviceUpdate(
    deviceId: string,
    payload: DeviceUpdate,
  ): Observable<DeviceDto> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.http
      .patch<DeviceDto>(
        `${this.apiEndpoint}/device/${encodedDeviceId}`,
        payload,
        {
          params: new HttpParams().set('dryRun', true),
          context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
        },
      )
      .pipe(map((device) => this.normalizeDevice(device)));
  }

  removeDeviceFromCompany(deviceId: string): Observable<void> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.http.delete<void>(
      `${this.apiEndpoint}/device/${encodedDeviceId}?autoReleaseLicenses=true`,
    );
  }
  deleteDevice(deviceId: string): Observable<void> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.http.delete<void>(
      `${this.apiEndpoint}/device/${encodedDeviceId}?permanently=true&autoReleaseLicenses=true`,
    );
  }
}
